/**
 * Persistência de sessão do PWA.
 *
 * Mantém o estado do usuário ao fechar e reabrir o app instalado:
 * - Antes do clique em "Começar agora" (CTA da Landing), o app PERMANECE
 *   na Landing ao reabrir — nada é restaurado, garantindo que o fluxo
 *   inicial não é "redefinido prematuramente".
 * - Depois do CTA, marcamos `flow_started=true` e passamos a registrar
 *   a última rota navegada. Ao reabrir o PWA (que sempre volta para "/"),
 *   restauramos o usuário para o ponto onde parou.
 */

const FLAG_KEY = "zappy.session.flowStarted";
const ROUTE_KEY = "zappy.session.lastRoute";

// Rotas que NUNCA devem ser persistidas como "última rota":
// - "/" (Landing): seria um no-op
// - rotas efêmeras de OAuth/redirects internos
const NON_PERSISTED_PATHS = new Set<string>(["/", "/index"]);
const NON_PERSISTED_PREFIXES = ["/~oauth"];

const safeStorage = (): Storage | null => {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

export const isFlowStarted = (): boolean => {
  const s = safeStorage();
  if (!s) return false;
  try {
    return s.getItem(FLAG_KEY) === "1";
  } catch {
    return false;
  }
};

export const markFlowStarted = (): void => {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(FLAG_KEY, "1");
  } catch {
    /* noop */
  }
};

export const clearPersistedSession = (): void => {
  const s = safeStorage();
  if (!s) return;
  try {
    s.removeItem(FLAG_KEY);
    s.removeItem(ROUTE_KEY);
    // Limpa chaves órfãs de versões antigas que ainda usem o prefixo
    // `zappy.session.*` (ex.: telemetria/feature flags descontinuados).
    const orphanKeys: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const key = s.key(i);
      if (key && key.startsWith("zappy.session.") && key !== FLAG_KEY && key !== ROUTE_KEY) {
        orphanKeys.push(key);
      }
    }
    orphanKeys.forEach((k) => s.removeItem(k));
  } catch {
    /* noop */
  }
};

export const setPersistedRoute = (pathname: string): void => {
  if (!pathname) return;
  if (NON_PERSISTED_PATHS.has(pathname)) return;
  if (NON_PERSISTED_PREFIXES.some((p) => pathname.startsWith(p))) return;
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(ROUTE_KEY, pathname);
  } catch {
    /* noop */
  }
};

export const getPersistedRoute = (): string | null => {
  const s = safeStorage();
  if (!s) return null;
  try {
    const value = s.getItem(ROUTE_KEY);
    if (!value) return null;
    if (NON_PERSISTED_PATHS.has(value)) return null;
    return value;
  } catch {
    return null;
  }
};
